import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import DataTable, { Column } from "../components/DataTable";
import AdminDataModal, { FormField } from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useFormat } from "../../context/FormatContext";
import "../styles/AdminTestimonials.css";

type TestimonialStatus = "active" | "inactive";

interface TestimonialRow {
  id: number;
  name: string;
  title: string;
  company: string;
  testimonial_text: string;
  external_link: string;
  image: string;
  sort_order: number;
  is_featured: number;
  status: TestimonialStatus;
  createdAt: string;
  updatedAt: string;
}

type ModalMode = "add" | "edit" | "view";

const API_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api";
const IMAGE_URL =
  import.meta.env.VITE_LYRAZONE_IMAGE_URL || "http://localhost:5000";

const defaultRowData = {
  id: "",
  name: "",
  title: "",
  company: "",
  testimonial_text: "",
  external_link: "",
  image: "",
  sort_order: 0,
  is_featured: 0,
  status: "active",
};

const AdminTestimonials: React.FC = () => {
  const { showModal } = useMessageModal();
  const { formatDateToDMY } = useFormat();

  const printRef = useRef<HTMLDivElement>(null);

  const [rows, setRows] = useState<TestimonialRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<TestimonialRow | null>(null);
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const [modalRowData, setModalRowData] = useState<any>(defaultRowData);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${API_URL}/admin/testimonials`, {
        withCredentials: true,
      });

      const data = Array.isArray(response.data?.data) ? response.data.data : [];

      setRows(data);
    } catch (error: any) {
      console.error("fetchTestimonials error:", error);

      setRows([]);

      showModal({
        type: "error",
        title: "Error",
        message: "Unable to fetch testimonials.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setModalMode("add");
    setModalRowData(defaultRowData);
    setIsModalOpen(true);
  };

  const handleView = (row: TestimonialRow) => {
    setModalMode("view");
    setModalRowData(row);
    setIsModalOpen(true);
  };

  const handleEdit = (row: TestimonialRow) => {
    setModalMode("edit");
    setModalRowData(row);
    setIsModalOpen(true);
  };

  const handleDelete = async (row: TestimonialRow) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete testimonial "${row.name}"?`,
    );
    if (!confirmed) return;

    try {
      await axios.delete(`${API_URL}/admin/testimonials/${row.id}`, {
        withCredentials: true,
      });

      showModal({
        type: "success",
        title: "Success",
        message: "Testimonial deleted successfully.",
      });

      setSelectedRow(null);
      fetchTestimonials();
    } catch (error: any) {
      console.error("delete testimonial error:", error);
      showModal({
        type: "error",
        title: "Error",
        message:
          error?.response?.data?.message || "Unable to delete testimonial.",
      });
    }
  };

  const handleSubmit = async (formData: any) => {
    if (
      !String(formData.name || "").trim() ||
      !String(formData.title || "").trim() ||
      !String(formData.testimonial_text || "").trim()
    ) {
      showModal({
        type: "confirm",
        title: "Validation",
        message: "Name, title and testimonial text are required.",
      });
      return;
    }

    try {
      const payload = new FormData();
      payload.append("name", formData.name || "");
      payload.append("title", formData.title || "");
      payload.append("company", formData.company || "");
      payload.append("testimonial_text", formData.testimonial_text || "");
      payload.append("external_link", formData.external_link || "");
      payload.append("sort_order", String(formData.sort_order || 0));
      payload.append("is_featured", String(formData.is_featured ?? 0));
      payload.append("status", formData.status || "active");

      if (formData.image?.file) {
        payload.append("image", formData.image.file);
      }

      if (modalMode === "add") {
        await axios.post(`${API_URL}/admin/testimonials`, payload, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });

        showModal({
          type: "success",
          title: "Success",
          message: "Testimonial added successfully.",
        });
      } else {
        await axios.put(
          `${API_URL}/admin/testimonials/${formData.id}`,
          payload,
          {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
          },
        );

        showModal({
          type: "success",
          title: "Success",
          message: "Testimonial updated successfully.",
        });
      }

      setIsModalOpen(false);
      setSelectedRow(null);
      setModalRowData(defaultRowData);
      fetchTestimonials();
    } catch (error: any) {
      console.error("save testimonial error:", error);
      showModal({
        type: "error",
        title: "Error",
        message:
          error?.response?.data?.message || "Unable to save testimonial.",
      });
    }
  };

  const handlePrint = (printRows: TestimonialRow[]) => {
    const printWindow = window.open("", "", "width=1200,height=800");
    if (!printWindow) return;

    const tableRows = printRows
      .map(
        (item) => `
          <tr>
            <td>${item.id}</td>
            <td>${item.name || "-"}</td>
            <td>${item.title || "-"}</td>
            <td>${item.company || "-"}</td>
            <td>${item.testimonial_text || "-"}</td>
            <td>${Number(item.is_featured) === 1 ? "Yes" : "No"}</td>
            <td>${item.status || "-"}</td>
            <td>${
              item.createdAt ? new Date(item.createdAt).toLocaleString() : "-"
            }</td>
          </tr>
        `,
      )
      .join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>Testimonials Print</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h2 { margin-bottom: 16px; }
            table { width: 100%; border-collapse: collapse; }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
              vertical-align: top;
              font-size: 12px;
            }
            th { background: #f5f5f5; }
          </style>
        </head>
        <body>
          <h2>Testimonials</h2>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Title</th>
                <th>Company</th>
                <th>Testimonial</th>
                <th>Featured</th>
                <th>Status</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const formFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "ID", colSpan: 1 },
      { key: "name", label: "Name", type: "text", colSpan: 1 },
      { key: "title", label: "Title", type: "text", colSpan: 1 },
      { key: "company", label: "Company", type: "text", colSpan: 1 },
      {
        key: "sort_order",
        label: "Sort Order",
        type: "number",
        colSpan: 1,
      },
      {
        key: "is_featured",
        label: "Featured",
        type: "select",
        options: [
          { label: "Yes", value: 1 },
          { label: "No", value: 0 },
        ],
        colSpan: 1,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: [
          { label: "Active", value: "active" },
          { label: "Inactive", value: "inactive" },
        ],
        colSpan: 1,
      },
      {
        key: "external_link",
        label: "External Link",
        type: "text",
        colSpan: 2,
      },
      {
        key: "testimonial_text",
        label: "Testimonial",
        type: "textarea",
        colSpan: 2,
      },
      {
        key: "image",
        label: "Image",
        type: "image",
        imageType: "single",
        imageFolder: "testimonials" as any,
        colSpan: 1,
      },
    ],
    [],
  );

  const columns: Column[] = [
    { key: "id", label: "ID" },
    {
      key: "image",
      label: "Image",
      render: (row: TestimonialRow) =>
        row.image ? (
          <img
            src={`${IMAGE_URL}/uploads/testimonials/${row.image}`}
            alt={row.name}
            className="admin-testimonial-thumb"
          />
        ) : (
          "-"
        ),
    },
    { key: "name", label: "Name" },
    { key: "title", label: "Title" },
    { key: "company", label: "Company" },
    {
      key: "testimonial_text",
      label: "Testimonial",
      render: (row: TestimonialRow) => (
        <span className="admin-testimonial-text-clamp">
          {row.testimonial_text}
        </span>
      ),
    },
    { key: "sort_order", label: "Sort Order" },
    {
      key: "is_featured",
      label: "Featured",
      render: (row: TestimonialRow) => (
        <span
          className={
            Number(row.is_featured) === 1 ? "badge-success" : "badge-danger"
          }
        >
          {Number(row.is_featured) === 1 ? "Yes" : "No"}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row: TestimonialRow) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Created",
      render: (row: TestimonialRow) =>
        row.createdAt ? formatDateToDMY(row.createdAt) : "-",
    },
  ];

  return (
    <div className="admin-page-wrap">
      <AdminListToolkit
        title="Testimonials"
        columns={columns}
        data={rows}
        selectedRow={selectedRow}
        onAdd={handleAdd}
        onView={handleView}
        onEdit={handleEdit}
        onSoftDelete={handleDelete}
        enableExport
        exportFileName="testimonials"
        extraButtons={[
          {
            label: "Print",
            onClick: () => handlePrint(rows),
          },
        ]}
      >
        {(filteredData: any) => {
          const dataArray = Array.isArray(filteredData)
            ? filteredData
            : filteredData?.data || [];

          return (
            <>
              <DataTable
                columns={columns}
                data={filteredData}
                loading={loading ? "Loading..." : ""}
                selectedRowId={selectedRow?.id ?? null}
                onRowSelect={(row: TestimonialRow) => setSelectedRow(row)}
              />

              <div ref={printRef} style={{ display: "none" }}>
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Title</th>
                      <th>Company</th>
                      <th>Testimonial</th>
                      <th>Featured</th>
                      <th>Status</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataArray.map((item: TestimonialRow) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>{item.name}</td>
                        <td>{item.title}</td>
                        <td>{item.company}</td>
                        <td>{item.testimonial_text}</td>
                        <td>{Number(item.is_featured) === 1 ? "Yes" : "No"}</td>
                        <td>{item.status}</td>
                        <td>
                          {item.createdAt
                            ? new Date(item.createdAt).toLocaleString()
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          );
        }}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={isModalOpen}
        mode={modalMode}
        title="Testimonial"
        fields={formFields}
        rowData={modalRowData}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        onFormChange={setModalRowData}
        size="lg"
      />
    </div>
  );
};

export default AdminTestimonials;
